import type { Language } from "@/hooks/useLanguage";
import type { SponsoredHolding } from "./types";
import {
	getSponsoredDisplayCopy,
	RANK_META,
	normalizePosterText,
	truncatePosterText,
} from "./utils";

export type PosterVariant = "desktop" | "mobile";

export const DESKTOP_POSTER_WIDTH = 1760;
export const DESKTOP_POSTER_HEIGHT = 720;
export const MOBILE_POSTER_WIDTH = 900;
export const MOBILE_POSTER_HEIGHT = 600;

export function getPosterDimensions(variant: PosterVariant) {
	return variant === "mobile"
		? { width: MOBILE_POSTER_WIDTH, height: MOBILE_POSTER_HEIGHT }
		: { width: DESKTOP_POSTER_WIDTH, height: DESKTOP_POSTER_HEIGHT };
}

export function buildPosterBackgroundUrl(rank: number, variant: PosterVariant) {
	const fileIndex = String(Math.min(rank + 1, 3)).padStart(2, "0");
	const suffix = variant === "mobile" ? "mobile" : "pc";
	return `/images/plazabanner/${fileIndex}-${suffix}.png`;
}

export function getPosterPalette(rank: number) {
	const medal = RANK_META[Math.min(rank, RANK_META.length - 1)];
	const placeholderBackgrounds = [
		variantGradient(
			"linear-gradient(135deg, #3E2E0B 0%, #7E5D16 24%, #B58B2B 48%, #F0D27C 72%, #56400D 100%)",
			"linear-gradient(180deg, #3E2E0B 0%, #8B6319 34%, #C79A38 62%, #F0D27C 100%)",
		),
		variantGradient(
			"linear-gradient(135deg, #1D2530 0%, #556175 28%, #A8B2C1 54%, #E6EDF5 78%, #3A4450 100%)",
			"linear-gradient(180deg, #1D2530 0%, #667387 36%, #AEB9C8 68%, #EEF3F8 100%)",
		),
		variantGradient(
			"linear-gradient(135deg, #3A1F17 0%, #7D4830 28%, #B26C48 56%, #D8A17A 80%, #51271C 100%)",
			"linear-gradient(180deg, #3A1F17 0%, #875039 34%, #BD7651 66%, #E1B08A 100%)",
		),
	] as const;

	const glowColors = [
		"rgba(247, 215, 116, 0.34)",
		"rgba(232, 237, 245, 0.28)",
		"rgba(217, 161, 122, 0.3)",
	] as const;

	return {
		medal,
		textColor: "#F8FAFC",
		secondaryText: "rgba(248,250,252,0.8)",
		panelBorder: "rgba(255,255,255,0.12)",
		panelBackground: "rgba(5,10,20,0.56)",
		panelGlow: medal.glow,
		accentSoft: "#A9D3FF",
		placeholderBackground: placeholderBackgrounds[Math.min(rank, placeholderBackgrounds.length - 1)],
		placeholderGlow: glowColors[Math.min(rank, glowColors.length - 1)],
	};
}

export function getPosterContent(
	holding: SponsoredHolding,
	variant: PosterVariant,
	language: Language,
) {
	const descriptionLimit = variant === "mobile" ? 72 : 132;
	const displayCopy = truncatePosterText(
		getSponsoredDisplayCopy(holding.token, language),
		descriptionLimit,
	);
	const ticker = normalizePosterText(holding.token.ticker) || holding.token.ticker;
	const tokenName = normalizePosterText(holding.token.name) || holding.token.name;
	const tokenId = normalizePosterText(holding.token.id) || holding.token.id;

	return {
		displayCopy,
		ticker,
		tokenName,
		tokenId,
		monogram: Array.from(ticker)[0] ?? "?",
	};
}

export function getPosterLayout(variant: PosterVariant) {
	if (variant === "mobile") {
		return {
			width: MOBILE_POSTER_WIDTH,
			height: MOBILE_POSTER_HEIGHT,
			radius: 34,
			icon: {
				containerSize: 180,
				size: 156,
				top: 94,
				borderWidth: 3,
			},
			content: {
				paddingX: 28,
				paddingBottom: 28,
				panelWidth: 500,
				panelPadding: "16px 18px 18px",
				panelRadius: 26,
				gap: 8,
			},
			text: {
				tickerSize: 44,
				nameSize: 20,
				nameTickerGap: 12,
				nameBadgeSize: 18,
				descriptionSize: 26,
				idSize: 16,
				nameMaxWidth: 420,
				descriptionMaxWidth: 420,
			},
			overlay:
				"linear-gradient(180deg, rgba(4,8,16,0.04) 0%, rgba(4,8,16,0.08) 44%, rgba(4,8,16,0.32) 66%, rgba(4,8,16,0.84) 100%)",
		};
	}

	return {
		width: DESKTOP_POSTER_WIDTH,
		height: DESKTOP_POSTER_HEIGHT,
		radius: 38,
		icon: {
			containerSize: 212,
			size: 188,
			top: 104,
			borderWidth: 4,
		},
		content: {
			paddingX: 42,
			paddingBottom: 42,
			panelWidth: 720,
			panelPadding: "22px 28px 24px",
			panelRadius: 34,
			gap: 14,
		},
		text: {
			tickerSize: 58,
			nameSize: 24,
			nameTickerGap: 16,
			nameBadgeSize: 22,
			descriptionSize: 31,
			idSize: 20,
			nameMaxWidth: 620,
			descriptionMaxWidth: 620,
		},
		overlay:
			"linear-gradient(90deg, rgba(3,6,12,0.78) 0%, rgba(4,7,13,0.68) 24%, rgba(4,7,13,0.34) 44%, rgba(4,7,13,0.08) 62%, rgba(4,7,13,0.12) 100%)",
	};
}

function variantGradient(desktop: string, mobile: string) {
	return {
		desktop,
		mobile,
	};
}
